import { Control, Controller, FieldValues, Path } from 'react-hook-form'
import { DateInputPropsType, FormDateInputFieldProps } from '../../utils/types'
import DateInput, { DateTimeInputComponent } from './DateInput'
import { DATE_FIELD_TYPES, DATE_PICKER_CONSTANTS, NAMESPACE } from '../../utils'
import { formatFormDateOnlyDisplayValue } from '../../utils/datePickerHelpers'
import { memo, useCallback, useMemo, useRef } from 'react'

const DateInputWrapper = memo(function DateInputWrapper({
  label,
  name,
  className,
  showClear,
  isDisabled,
  handleChange,
  placeholder,
  showIcon,
  tabIndex,
  value,
  defaultValue,
  error,
  required,
  max,
  min,
  type = DATE_FIELD_TYPES.DATE_TYPE,
  hasPermission = true,
  dataQaId,
  borderStyles,
}: DateInputPropsType & {
  name: string
  error?: string
  hasPermission?: boolean
  type?: DATE_FIELD_TYPES
}) {
  const hasValue = !!value || !!defaultValue || !!placeholder

  const dateOnlyValue = useMemo(() => {
    if (type !== DATE_FIELD_TYPES.DATE_TYPE) return value
    return formatFormDateOnlyDisplayValue(value)
  }, [type, value])

  return (
    <div data-qa-id={dataQaId ?? name} className="w-full font-primary">
      <div
        className={`group relative w-full custom-input-main-container font-primary date-input-custom-wrapper`}
      >
        {label && (
          <label
            className={`absolute ${
              isDisabled ? 'text-accent-300' : 'text - black'
            } left-3 z-10 transition-all duration-200 ease-in-out 
      pointer-events-none truncate max-w-[calc(100%-1.75rem)]
      font-primary font-extralight  ${
        isDisabled
          ? 'text-accent-300'
          : 'text-black group-hover:text-secondary-500'
      }
                  ${
                    hasValue
                      ? `-top-[.5rem] text-xs bg-white px-1 pb-[0] group-focus-within:text-secondary-500`
                      : `group-focus-within:-top-[.5rem] group-focus-within:text-xs group-focus-within:bg-white group-focus-within:px-1 group-focus-within:pb-[0] top-[.9rem] text-xs`
                  }
                `}
          >
            {label}
            {required && (
              <span
                className={`ml-1 ${
                  isDisabled ? 'text-accent-300' : 'text-error-500'
                }`}
              >
                *
              </span>
            )}
          </label>
        )}

        <div
          className={`custom-input-container w-full inline-flex items-center font-light px-3 py-1.5 rounded border 
                ${
                  isDisabled
                    ? 'border-border-200 bg-accent-50 cursor-not-allowed text-accent-400'
                    : `${borderStyles} bg-white group-focus-within:border-secondary-500 group-hover:border-secondary-500 hover:border-secondary-500`
                } ${className}`}
        >
          {!hasPermission ? (
            <input
              value={NAMESPACE.REDACTED}
              disabled
              className="w-full bg-transparent outline-none border-none text-xs p-[0]"
            />
          ) : null}
          {hasPermission &&
            (type === DATE_FIELD_TYPES.DATE_TYPE ? (
              <DateInput
                name={name}
                isDisabled={isDisabled}
                value={dateOnlyValue}
                handleChange={handleChange}
                className={DATE_PICKER_CONSTANTS.FORM_INNER_INPUT_CLASSNAME}
                tabIndex={tabIndex}
                showClear={showClear}
                showIcon={showIcon}
                max={max}
                min={min}
                placeholder={placeholder}
              />
            ) : (
              <DateTimeInputComponent
                name={name}
                isDisabled={isDisabled}
                value={value}
                handleChange={handleChange}
                className={DATE_PICKER_CONSTANTS.FORM_INNER_INPUT_CLASSNAME}
                tabIndex={tabIndex}
                showClear={showClear}
                showIcon={showIcon}
                max={max}
                min={min}
                placeholder={placeholder}
              />
            ))}
        </div>
        {error && (
          <span className="text-error-500 text-xxs mt-1 block font-primary">
            {error}
          </span>
        )}
      </div>
    </div>
  )
})

function FormDateInputField<U extends FieldValues>({
  field,
  fieldError,
  onExternalChange,
  ...wrapperProps
}: FormDateInputFieldProps<U>) {
  const onExternalChangeRef = useRef(onExternalChange)
  onExternalChangeRef.current = onExternalChange

  const handleDateChange = useCallback(
    (date?: string | null) => {
      field.onChange(date)
      onExternalChangeRef.current?.(date)
    },
    [field.onChange]
  )

  return (
    <DateInputWrapper
      {...wrapperProps}
      name={field.name}
      value={(field.value ?? wrapperProps.defaultValue) as string}
      handleChange={handleDateChange}
      error={fieldError}
    />
  )
}

const FormDateInput = <U extends FieldValues>({
  label,
  name,
  className,
  showClear,
  isDisabled,
  control,
  handleChange,
  showIcon,
  tabIndex,
  defaultValue,
  max,
  min,
  placeholder,
  required,
  hasPermission,
  type,
  dataQaId,
  showErrors = true,
  borderStyles = 'border-border-150',
}: DateInputPropsType & {
  name: Path<U>
  control: Control<U>
  min: string
  max: string
  hasPermission?: boolean
  type?: DATE_FIELD_TYPES
}) => {
  const validationRules = useMemo(() => {
    if (typeof required === 'string') {
      return { required }
    }
    if (required) {
      return { required: NAMESPACE?.REQUIRED_FIELD }
    }
    return { required: false as const }
  }, [required])

  return (
    <Controller
      name={name}
      control={control}
      rules={validationRules}
      render={({ field, fieldState }) => (
        <div className={`relative w-[100%] focus:outline-none ${className}`}>
          <FormDateInputField
            field={field}
            fieldError={showErrors ? fieldState?.error?.message : undefined}
            onExternalChange={handleChange}
            name={name}
            isDisabled={isDisabled}
            label={label}
            required={required}
            className={className}
            showIcon={showIcon}
            showClear={showClear}
            tabIndex={tabIndex}
            defaultValue={defaultValue}
            max={max}
            min={min}
            placeholder={placeholder}
            hasPermission={hasPermission}
            type={type}
            dataQaId={dataQaId}
            borderStyles={borderStyles}
          />
        </div>
      )}
    />
  )
}

export default FormDateInput
